import { Link } from '@remix-run/react';
import { useMemo } from 'react';

import ProductCard from '~/components/ProductCard';
import EmblaSlider from '~/components/Slider/EmblaSlider';
import { useViewItemList, type ListContext } from '~/gtm';
import { Product, ProductSliderBanner } from '~/types/Product';

interface ProductsSliderProps {
  title: string;
  description?: string;
  products: Product[];
  banner?: ProductSliderBanner;
  listContext?: ListContext;
  currency?: string;
  centeredHeader?: boolean;
}

const ProductsSlider = ({
  title,
  description,
  products,
  banner,
  listContext,
  currency = 'CZK',
  centeredHeader = true,
}: ProductsSliderProps) => {
  // Memoize default list context to prevent unnecessary re-renders
  const effectiveListContext = useMemo<ListContext>(
    () => listContext || { item_list_id: 'products_slider', item_list_name: title },
    [listContext, title]
  );

  // GTM: Track view_item_list with IntersectionObserver
  const { observeProduct } = useViewItemList({
    listContext: effectiveListContext,
    currency,
    products,
  });

  const slides = [
    ...(banner?.image
      ? [
          <div className="products-slider__banner" key="banner">
            <Link to={banner.link || '#'} className="products-slider__banner-link">
              <img
                src={banner.image}
                alt={banner.title}
                className="products-slider__banner-image"
                // Approximates the slide's rendered box (~1/5 of the 1344px container, .products-slider__banner
                // min-height: 375px at lg+); CSS still fills it via absolute positioning, width:100%/height:100%.
                width={264}
                height={375}
                loading="lazy"
                fetchpriority="low"
                decoding="async"
              />
              <div className="products-slider__banner-content">
                <p className="products-slider__banner-subtitle">{banner.subtitle}</p>
                <strong className="h3 products-slider__banner-title">{banner.title}</strong>
                {banner.buttonTitle && (
                  <span className="products-slider__banner-button btn btn-brand">{banner.buttonTitle}</span>
                )}
              </div>
            </Link>
          </div>,
        ]
      : []),
    ...products.map((product, index) => (
      <ProductCard
        product={product}
        key={product.id}
        listContext={effectiveListContext}
        index={index}
        onProductRef={observeProduct}
      />
    )),
  ];

  return (
    <div className="products-slider">
      <h3 className={`products-slider__title${centeredHeader ? ' text-center' : ''} ${!description ? 'mb-3' : ''}`}>
        {title}
      </h3>
      {description && (
        <p className={`products-products-slider__description${centeredHeader ? ' text-center' : ''}`}>{description}</p>
      )}
      <EmblaSlider slideClass="products-slider__slide" className="embla--branded">
        {slides}
      </EmblaSlider>
    </div>
  );
};

export default ProductsSlider;
